import {
    CodeActivateSuccessResponse,
    FormRendezvousFields,
} from '@tn/foundation/graphql/__generated/gql/graphql';
import request from 'graphql-request';
import { fromPromise } from 'xstate';

import { FlowEntity } from '../../types';
import { cleanFormData } from '../../utils/cleanFormData/cleanFormData';
import { devLog } from '../../utils/devLog';

import { formRendezvousMutation } from './graphql/constants';

const normalizeActivationCode = (activationCode: string) => {
    return activationCode
        .replace(/\s+/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');
};

type SubmitFormTvParentContext = {
    activationCode: string;
    lookupOnly?: boolean;
};

type SubmitFormTvOutput = Omit<CodeActivateSuccessResponse, 'device'> & {
    device?: {
        name?: string;
        partner?: string;
    };
};

export const submitFormTvInitActor = () =>
    fromPromise<SubmitFormTvOutput, FormRendezvousFields>(async ({ input }) => {
        devLog('[submitFormTvActor] submitFormTvInitActor input', () => input);
        const cleanedFormData = cleanFormData(input);
        const graphqlEndpoint = '/api/tn/signup/graphql/';
        const normalizedActivationCode =
            typeof cleanedFormData.activationCode === 'string'
                ? normalizeActivationCode(cleanedFormData.activationCode)
                : '';

        if (!normalizedActivationCode) {
            throw new Error('Activation code is required');
        }

        cleanedFormData.activationCode = normalizedActivationCode;

        if (input.lookupOnly) {
            cleanedFormData.lookupOnly = true;
        }

        const response = await request<{
            formRendezvousUpload?: CodeActivateSuccessResponse | null;
        }>(graphqlEndpoint, formRendezvousMutation, {
            formFields: cleanedFormData as FormRendezvousFields,
        });

        if (!response.formRendezvousUpload) {
            throw new Error('Rendezvous mutation response did not contain expected data.');
        }

        const result = response.formRendezvousUpload;

        if (!result.success) {
            throw new Error(result.message || 'Activation failed');
        }

        return {
            ...result,
            device: result.device
                ? {
                      name: result.device.name ?? undefined,
                      partner: result.device.partner ?? undefined,
                  }
                : undefined,
        };
    });

export const submitFormTvActor: FlowEntity = {
    initActor: submitFormTvInitActor,
    inputActor: ({ context }: { context: SubmitFormTvParentContext }): FormRendezvousFields => {
        return {
            activationCode: context.activationCode ?? '',
            lookupOnly: context.lookupOnly,
        };
    },
};
